import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";

const Signup = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast.error("Veuillez accepter les conditions générales de vente");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoading(true);

    try {
      const result = await register(formData);
      if (result.success) {
        toast.success("Compte créé ! Vérifiez votre email.");
        navigate("/verify-email-pending", { state: { email: formData.email } });
      } else {
        toast.error(result.message || "Erreur lors de la création du compte");
      }
    } catch (error) {
      console.error("Signup error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création du compte");
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    "QR code personnalisé pour votre établissement",
    "Gestion intelligente des avis clients",
    "Tableau de bord complet",
    "Support dédié"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 flex">
      {/* Left Panel - Benefits */}
      <div className="hidden lg:flex flex-col justify-center w-1/2 bg-gradient-to-br from-slate-900 to-indigo-950 p-12 text-white">
        <div className="max-w-md">
          <h2 className="text-4xl font-bold font-['Plus_Jakarta_Sans'] mb-6">
            Transformez vos avis en opportunité
          </h2>
          <p className="text-slate-300 text-lg mb-8 leading-relaxed">
            Rejoignez des centaines de professionnels qui utilisent ResponsIA pour améliorer leur e-réputation.
          </p>
          <ul className="space-y-4">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span className="text-slate-200">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-4 lg:p-12">
        <div className="w-full max-w-md">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour à l'accueil</span>
          </Link>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900 mb-2">
                Créer un compte
              </h1>
              <p className="text-slate-500">
                Commencez gratuitement avec ResponsIA
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-slate-700">Prénom</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    type="text"
                    placeholder="Jean"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white"
                    data-testid="signup-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-slate-700">Nom</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    type="text"
                    placeholder="Dupont"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white"
                    data-testid="signup-lastname-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white"
                  data-testid="signup-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white pr-12"
                    data-testid="signup-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-400">Minimum 6 caractères</p>
              </div>

              {/* CGV Acceptance Checkbox */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="accept-terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  data-testid="signup-terms-checkbox"
                />
                <label htmlFor="accept-terms" className="text-sm text-slate-600">
                  J'accepte les{" "}
                  <Link to="/cgv" target="_blank" className="text-violet-600 hover:underline font-medium">
                    Conditions Générales de Vente
                  </Link>{" "}
                  et la{" "}
                  <Link to="/confidentialite" target="_blank" className="text-violet-600 hover:underline font-medium">
                    Politique de Confidentialité
                  </Link>
                  <span className="text-red-500"> *</span>
                </label>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 rounded-full bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-90"
                disabled={loading}
                data-testid="signup-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  "Créer mon compte"
                )}
              </Button>
            </form>

            {/* Footer */}
            <div className="mt-6 text-center">
              <p className="text-slate-500">
                Déjà un compte ?{" "}
                <Link to="/login" className="text-violet-600 hover:text-violet-700 font-medium">
                  Se connecter
                </Link>
              </p>
            </div>
          </div>

          {/* Terms */}
          <p className="mt-6 text-center text-sm text-slate-400">
            En créant un compte, vous acceptez nos{" "}
            <Link to="/cgv" className="text-slate-600 hover:underline">conditions générales de vente</Link> et notre{" "}
            <Link to="/confidentialite" className="text-slate-600 hover:underline">politique de confidentialité</Link>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
